import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Агент представлений: фраза на входе, собранный экран на выходе.
//
// Страница ничего не знает ни про каталог, ни про описание — она отправляет
// фразу и рисует то, что вернул агент. Пока за `llm-gate` заглушка, агент
// работает в режиме воспроизведения записанных пар «фраза → описание»; это
// видно по значку рядом с ответом и меняется одним env, без правок здесь.
import { useState, type ReactElement } from 'react';
import { Alert, Button, Card, Collapse, Input, Space, Tag, Typography } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { LIST_SURFACE_STYLE } from '../../../shared/ui/list-styles.js';
import { QuietListSkeleton } from '../../../shared/ui/quiet-states.js';
import { VIEW_FIXTURES } from '../../../views/fixtures.js';
import { askViewsAgent, type ViewsAgentTurn } from '../api/views.client.js';
import { ViewRenderer } from '../components/ViewRenderer.js';
import { SaveViewModal } from '../components/SaveViewModal.js';
import { useCreateSavedView, useSavedViews } from '../hooks/useSavedViews.js';
import { useAgentWidgets, useDeleteAgentWidget } from '../../dashboard/hooks/useAgentWidgets.js';

const { Text } = Typography;

export default function ViewsPage(): ReactElement {
  useI18nSubscription();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [pinOpen, setPinOpen] = useState(false);
  const savedViews = useSavedViews();
  const agentWidgets = useAgentWidgets();
  const deleteAgentWidget = useDeleteAgentWidget();
  const createView = useCreateSavedView();

  const ask = useMutation<ViewsAgentTurn, Error, string>({ mutationFn: askViewsAgent });
  const turn = ask.data;

  const send = (text: string): void => {
    const trimmed = text.trim();
    if (trimmed === '') return;
    setPrompt(trimmed);
    ask.mutate(trimmed);
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title={i18n.t('crm.features.features.views.pages.viewspage.агент_представлений')}
        subtitle={i18n.t(
          'crm.features.features.views.pages.viewspage.опишите_словами_что_хотите_видеть_агент_св',
        )}
      />

      <Card size="small">
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Input.Search
            size="large"
            allowClear
            enterButton={i18n.t('crm.features.features.views.pages.viewspage.показать')}
            placeholder={i18n.t(
              'crm.features.features.views.pages.viewspage.например_три_последних_договора_сумма_фирм',
            )}
            value={prompt}
            loading={ask.isPending}
            onChange={(event) => setPrompt(event.target.value)}
            onSearch={send}
          />
          <Space wrap>
            <Text type="secondary">
              {i18n.t('crm.features.features.views.pages.viewspage.примеры')}
            </Text>
            {VIEW_FIXTURES.map((fixture) => (
              <Button key={fixture.prompt} size="small" onClick={() => send(fixture.prompt)}>
                {fixture.prompt}
              </Button>
            ))}
          </Space>
        </Space>
      </Card>

      {/* Quiet loading (the shared list chrome) instead of a bare Spin. */}
      {ask.isPending ? (
        <div style={LIST_SURFACE_STYLE}>
          <QuietListSkeleton rows={3} />
        </div>
      ) : null}
      {ask.isError ? (
        <ErrorState
          title={i18n.t('crm.features.features.views.pages.viewspage.агент_не_ответил')}
          error={ask.error}
          variant="inline"
          onRetry={() => send(prompt)}
        />
      ) : null}

      {turn === undefined ? null : (
        <Card size="small">
          <Space align="start">
            <Text>{turn.reply}</Text>
            {turn.mode === 'fixtures' ? (
              // Честно помечаем: живой модели за шлюзом пока нет, ответ собран
              // из записанных пар. Всё остальное — словарь, разбор, права,
              // исполнение — работает по-настоящему.
              <Tag>
                {i18n.t('crm.features.features.views.pages.viewspage.по_записанным_примерам')}
              </Tag>
            ) : null}
          </Space>
        </Card>
      )}

      {turn?.issues === undefined ? null : (
        <Alert
          type="warning"
          showIcon
          title={i18n.t(
            'crm.features.features.views.pages.viewspage.описание_не_удалось_разобрать',
          )}
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {turn.issues.map((issue) => (
                <li key={`${issue.path}-${issue.message}`}>
                  {issue.message}
                  {issue.hint === undefined ? null : <Text type="secondary"> — {issue.hint}</Text>}
                </li>
              ))}
            </ul>
          }
        />
      )}

      {turn?.result === undefined ? null : (
        <>
          <div className="flex justify-end">
            <Button type="primary" onClick={() => setPinOpen(true)}>
              {i18n.t('crm.features.features.views.pages.viewspage.закрепить')}
            </Button>
          </div>
          <ViewRenderer result={turn.result} />
        </>
      )}

      {(savedViews.data?.length ?? 0) === 0 ? null : (
        <Card
          size="small"
          title={i18n.t('crm.features.features.views.pages.viewspage.закрепленные_представления')}
        >
          <Space wrap>
            {savedViews.data?.map((saved) => (
              <Button key={saved.id} onClick={() => navigate(`/views/${saved.id}`)}>
                {saved.title}
              </Button>
            ))}
          </Space>
        </Card>
      )}

      {(agentWidgets.data?.length ?? 0) === 0 ? null : (
        <Card size="small" title={i18n.t('crm.dashboard.agent_widgets.title')}>
          <Space wrap>
            {agentWidgets.data?.map((widget) => (
              <Tag
                key={widget.id}
                closable
                onClose={(event) => {
                  event.preventDefault();
                  deleteAgentWidget.mutate(widget.id);
                }}
              >
                {widget.title}
              </Tag>
            ))}
          </Space>
        </Card>
      )}

      {turn?.spec === undefined ? null : (
        <>
          <Collapse
            size="small"
            items={[
              {
                key: 'spec',
                label: i18n.t(
                  'crm.features.features.views.pages.viewspage.описание_которое_собрал_агент',
                ),
                children: (
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(turn.spec, null, 2)}
                  </pre>
                ),
              },
            ]}
          />
          <SaveViewModal
            open={pinOpen}
            spec={turn.spec}
            saving={createView.isPending}
            error={createView.error}
            onCancel={() => setPinOpen(false)}
            onSubmit={async (payload) => {
              const saved = await createView.mutateAsync(payload);
              setPinOpen(false);
              navigate(`/views/${saved.id}`);
            }}
          />
        </>
      )}
    </div>
  );
}
